/**
 * Theme provider for in-app dark mode (issue #67).
 *
 * - `mode` is the user preference: 'system' | 'light' | 'dark'.
 * - The preference persists via GeneralStorage under KEYS.themeMode — a
 *   non-sensitive UI pref (plain AsyncStorage, same as KEYS.timeFormat;
 *   deliberately NOT in SENSITIVE_KEYS).
 * - 'system' follows RN Appearance. The native light-mode locks (Info.plist
 *   UIUserInterfaceStyle=Light, MainActivity MODE_NIGHT_NO) were removed at
 *   cutover, so 'system' genuinely tracks the OS scheme — and native dialogs
 *   (alerts, date/time pickers) now follow the OS on both platforms.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {Appearance, type ColorSchemeName} from 'react-native';

import {KEYS} from '../utils/Constants';
import {getData, storeData} from '../utils/GeneralStorage';
import {darkTheme, lightTheme, type ThemeColors} from './tokens';

export type ThemeMode = 'system' | 'light' | 'dark';

/**
 * Default preference. The migration (docs/DARK-MODE.md) is complete; 'light'
 * remains the DELIBERATE default — existing users see no change until they
 * opt in via My Account → Appearance ('dark' or 'system'). Product decision:
 * do not flip this to 'system' without one.
 */
export const DEFAULT_THEME_MODE: ThemeMode = 'light';

const THEME_MODES: readonly ThemeMode[] = ['system', 'light', 'dark'];

const isThemeMode = (value: unknown): value is ThemeMode =>
  typeof value === 'string' && (THEME_MODES as readonly string[]).includes(value);

export interface ThemeContextValue {
  /** Semantic colors for the resolved scheme — the only surface screens use. */
  readonly colors: ThemeColors;
  /** The stored user preference (may be 'system'). */
  readonly mode: ThemeMode;
  /** The preference after resolving 'system' against the OS scheme. */
  readonly resolvedScheme: 'light' | 'dark';
  /** Set + persist the preference. */
  readonly setMode: (mode: ThemeMode) => void;
}

/**
 * Default value = today's light palette with a no-op setter, so components
 * (and the many legacy tests that render screens bare) keep working outside
 * a provider. The real provider is mounted once at the app root (App.js).
 */
const ThemeContext = createContext<ThemeContextValue>({
  colors: lightTheme,
  mode: DEFAULT_THEME_MODE,
  resolvedScheme: 'light',
  setMode: () => undefined,
});

// RN 0.85: ColorSchemeName is 'light' | 'dark' | 'unspecified', and
// getColorScheme()/the change listener may also yield null | undefined.
type SystemScheme = ColorSchemeName | null | undefined;

const resolveScheme = (
  mode: ThemeMode,
  systemScheme: SystemScheme,
): 'light' | 'dark' => {
  if (mode === 'system') {
    return systemScheme === 'dark' ? 'dark' : 'light';
  }
  return mode;
};

export const ThemeProvider = ({children}: {children: React.ReactNode}) => {
  const [mode, setModeState] = useState<ThemeMode>(DEFAULT_THEME_MODE);
  // Anything other than 'dark' (including 'unspecified'/null/undefined)
  // resolves to light in resolveScheme.
  const [systemScheme, setSystemScheme] = useState<SystemScheme>(() =>
    Appearance.getColorScheme(),
  );

  // Hydrate the persisted preference once. Unknown / legacy values are
  // ignored so a bad stored string can never wedge the UI.
  useEffect(() => {
    let cancelled = false;
    getData(KEYS.themeMode).then(stored => {
      if (!cancelled && isThemeMode(stored)) {
        setModeState(stored);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Follow the OS scheme while (and only while) the preference is 'system'.
  useEffect(() => {
    if (mode !== 'system') {
      return undefined;
    }
    setSystemScheme(Appearance.getColorScheme());
    const subscription = Appearance.addChangeListener(({colorScheme}) =>
      setSystemScheme(colorScheme),
    );
    return () => subscription.remove();
  }, [mode]);

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next);
    // Fire-and-forget: storeData never rejects (it catches internally).
    storeData(KEYS.themeMode, next);
  }, []);

  const resolvedScheme = resolveScheme(mode, systemScheme);

  const value = useMemo<ThemeContextValue>(
    () => ({
      colors: resolvedScheme === 'dark' ? darkTheme : lightTheme,
      mode,
      resolvedScheme,
      setMode,
    }),
    [mode, resolvedScheme, setMode],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

/** Access the active theme: `const {colors, mode, setMode} = useTheme();` */
export const useTheme = (): ThemeContextValue => useContext(ThemeContext);
